import { Component } from 'react';
import * as Sentry from '@sentry/react';
import { ErrorState } from './primitives/ErrorState.jsx';

function isChunkLoadError(error) {
  return /Failed to fetch dynamically imported module|Loading chunk \d+ failed|Loading CSS chunk \d+ failed/i.test(
    String(error?.message || error)
  );
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    Sentry.captureException(error, {
      tags: { source: 'error-boundary' },
      extra: { componentStack: info?.componentStack },
    });
    // Stale chunk after a deployment — reload automatically to pull fresh assets.
    if (isChunkLoadError(error)) {
      window.location.reload();
    }
  }

  render() {
    if (this.state.error) {
      const isDev = import.meta.env?.DEV;
      const isChunkError = isChunkLoadError(this.state.error);
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-sitruna-cream p-6">
          <ErrorState
            variant="page"
            heading={isChunkError ? 'New version available' : 'Something went wrong.'}
            body={isChunkError ? 'Refreshing to load the latest version…' : 'Please refresh the page.'}
            onRetry={() => window.location.reload()}
          />
          {isDev && !isChunkError && (
            <pre className="mt-4 text-xs bg-white border border-n-200 rounded-lg p-3 max-w-lg w-full overflow-auto whitespace-pre-wrap text-n-700">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
